import { STANDARD_DATE_FORMAT } from '@/constants/formats';
import { InsuranceQuoteType, QuoteTypeCodes } from '@/interfaces/insurance-quote/quote.interface';
import { ICardDetails, IReviewSummary, IReviewSummaryItem, getInsuranceTypeDisplayName } from '@/interfaces';
import { IRenewalAcknowledgementModel } from '@/interfaces/decision-intelligence/decision-intelligence-request';
import { IDecisionIntelligenceOfferResponse } from '@/interfaces/decision-intelligence/decision-intelligence-response';
import { IAnalyticsForm } from '@/interfaces/forms/analytics-interface';
import { IHighlight } from '@/interfaces/forms/highlights-interface';
import {
  IApplyAssistanceQuoteDiscountRequest,
  IApttusDirectDebitRequest,
  IPayWithCardResponse,
  IPaymentReferenceDetails,
  IQuotedOption,
  IReceiptRequest,
} from '@/interfaces/make-a-payment/make-a-payment.interface';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { toCurrency } from '@/utils/convert';
import { format } from 'date-fns';

export enum Bookmarks {
  amountPaid = 'amountPaid',
  coverName = 'coverName',
  insuranceType = 'insuranceType',
  productType = 'productType',
  paymentReferenceNumber = 'paymentReferenceNumber',
  paymentDate = 'paymentDate',
  prn = 'prn',
  coverType = 'coverType',
  transactionType = 'transactionType',
  termsAndConditionLink = 'termsAndConditionLink',
  email = 'email',
  maskedEmail = 'maskedEmail',
}
export enum MakeAPaymentStepIds {
  Upgrade_Options = 'coverreview',
}
export const mapReceiptRequest = (
  prn: string,
  emailAddress: string,
  paymentReferenceDetails?: IPaymentReferenceDetails,
  paymentResponse?: IPayWithCardResponse
): IReceiptRequest => {
  return {
    pRN: prn,
    emailAddress: emailAddress,
    amount: paymentReferenceDetails?.amount,
    paymentResult: paymentResponse?.result,
    product: appendRACQ(paymentReferenceDetails?.policyTitle),
    receiptNumber: paymentResponse?.reference,
    isInsurance: paymentReferenceDetails?.isInurance,
  };
};

export const replacePolicySummaryBookmarks = (
  content: string,
  paymentReferenceDetails: IPaymentReferenceDetails,
  prn: string,
  dictionary: Record<string, string>,
  quotedOption?: IQuotedOption
) => {
  let replaceBookmarksContent = replaceBookmark(Bookmarks.paymentDate, content, format(Date(), STANDARD_DATE_FORMAT));

  replaceBookmarksContent = replaceBookmark(
    Bookmarks.amountPaid,
    replaceBookmarksContent,
    toCurrency(paymentReferenceDetails.amount?.toString() ?? '')
  );
  replaceBookmarksContent = replaceBookmark(
    Bookmarks.insuranceType,
    replaceBookmarksContent,
    getInsuranceTypeDisplayName(dictionary, paymentReferenceDetails?.coverageCode ?? '')
  );
  replaceBookmarksContent = replaceBookmark(
    Bookmarks.productType,
    replaceBookmarksContent,
    getProductType(paymentReferenceDetails, dictionary, quotedOption)
  );
  replaceBookmarksContent = replaceBookmark(
    Bookmarks.coverName,
    replaceBookmarksContent,
    paymentReferenceDetails.description
  );
  replaceBookmarksContent = replaceBookmark(Bookmarks.paymentReferenceNumber, replaceBookmarksContent, prn);
  return replaceBookmarksContent;
};
export const mapItems = (
  summaryItems: any,
  paymentReferenceDetails: IPaymentReferenceDetails,
  prn: string,
  dictionary: Record<string, string>,
  quotedOption?: IQuotedOption
) => {
  const mappedItems: IReviewSummaryItem[] = summaryItems.map((val: any) => {
    const item: IReviewSummaryItem = {
      title: {
        value: replacePolicySummaryBookmarks(
          val.fields.title.value,
          paymentReferenceDetails,
          prn,
          dictionary,
          quotedOption
        ),
      },
      content: {
        value: replacePolicySummaryBookmarks(
          val.fields.content.value,
          paymentReferenceDetails,
          prn,
          dictionary,
          quotedOption
        ),
      },
      showItem: true,
      showEdit: false,
      editUrl: '',
      icon: { value: '' },
    };
    return item;
  });
  return mappedItems.filter((x) => x.showItem);
};
export const mapSummaries = (
  formInputs: any,
  dictionary: Record<string, string>,
  paymentReferenceDetails: IPaymentReferenceDetails,
  prn: string,
  quotedOption?: IQuotedOption
): IReviewSummary[] => {
  const reviewSummaries = paymentReferenceDetails.isInurance
    ? formInputs?.reviewSummaryInsurance.fields.map((val: any) => {
        const summ: IReviewSummary = {
          summaryTitle: val.summaryTitle,
          icon: val.icon,
          formContentId: val.formContentId,
          items: mapItems(val.items, paymentReferenceDetails, prn, dictionary, quotedOption),
        };
        return summ;
      })
    : paymentReferenceDetails.isInvoiceDebt
      ? formInputs?.reviewSummaryInvoice.fields.map((val: any) => {
          const summ: IReviewSummary = {
            summaryTitle: val.summaryTitle,
            icon: val.icon,
            formContentId: val.formContentId,
            items: mapItems(val.items, paymentReferenceDetails, prn, dictionary, quotedOption),
          };
          return summ;
        })
      : formInputs?.reviewSummaryRoadside.fields.map((val: any) => {
          const summ: IReviewSummary = {
            summaryTitle: val.summaryTitle,
            icon: val.icon,
            formContentId: val.formContentId,
            items: mapItems(val.items, paymentReferenceDetails, prn, dictionary, quotedOption),
          };
          return summ;
        });
  return reviewSummaries;
};

export const mapRenewalAcknowledgement = (
  paymentReferenceDetails: IPaymentReferenceDetails,
  decisionOfferResponse: IDecisionIntelligenceOfferResponse,
  paymentAttempled: boolean,
  paymentSucceeded: boolean
): IRenewalAcknowledgementModel => {
  return {
    paymentReferenceDetails: paymentReferenceDetails,
    decisionOfferResponse: decisionOfferResponse,
    paymentAttempted: paymentAttempled,
    paymentSucceeded: paymentSucceeded,
  };
};

export const mapDiscountRequest = (selectedOption?: IQuotedOption): IApplyAssistanceQuoteDiscountRequest => {
  return {
    apttusCartId: selectedOption?.apttusCartId ?? '',
    apttusQuoteId: selectedOption?.apttusQuoteId ?? '',
  };
};

export const mapDirectDebitRequest = (
  paymentReferenceDetails: IPaymentReferenceDetails,
  cardDetails: ICardDetails
): IApttusDirectDebitRequest => {
  return {
    creditCard: {
      token: cardDetails.token,
      maskedCardNumber: cardDetails.maskedCardNumber,
      cardExpiry: `${cardDetails.cardExpiryMonth}/${cardDetails.cardExpiryYear}`,
      cardHolder: cardDetails.cardHoldersName,
      cardType: cardDetails.cardType,
    },
    assetLineItems: [
      {
        assetLineItemId: paymentReferenceDetails.assetLineItemId,
        enabled: true,
      },
    ],
  };
};
export const appendRACQ = (product?: string): string => {
  return !product?.includes('RACQ') ? `RACQ ${product}` : product;
};
export const getProductType = (
  paymentReferenceDetails: IPaymentReferenceDetails,
  dictionary: Record<string, string>,
  quotedOption?: IQuotedOption
): string => {
  if (paymentReferenceDetails?.coverageCode) {
    return paymentReferenceDetails.policyTitle
      ? paymentReferenceDetails.policyTitle
      : getInsuranceTypeDisplayName(dictionary, paymentReferenceDetails?.coverageCode ?? '');
  }
  return quotedOption ? appendRACQ(quotedOption.product) : appendRACQ(paymentReferenceDetails?.policyTitle ?? '');
};

const replaceBookAssistanceBookMarks = (
  content: string,
  paymentReferenceDetails: IPaymentReferenceDetails,
  isUpgrade?: boolean
): string => {
  content = replaceBookmark(
    Bookmarks.coverType,
    content,
    !paymentReferenceDetails.isNotARoadsideProduct
      ? 'roadside cover'
      : `${appendRACQ(paymentReferenceDetails.currentProduct.product)}${paymentReferenceDetails.currentProduct.product == 'Lifestyle' ? ' subscription' : ''}`
  );
  content = replaceBookmark(Bookmarks.transactionType, content, isUpgrade ? 'upgraded' : 'renewed');
  return content;
};

export const mapHighlights = (
  formInputs: any,
  paymentReferenceDetails: IPaymentReferenceDetails,
  dictionary: Record<string, string>,
  isUpgrade?: boolean,
  productCode?: string
): IHighlight[] => {
  if (paymentReferenceDetails.isInurance) {
    return [
      {
        content: replaceBookmark(
          Bookmarks.insuranceType,
          formInputs?.introCoverInsurance?.label?.value,
          getProductType(paymentReferenceDetails, dictionary)
        ),
      },
    ] as IHighlight[];
  } else if (paymentReferenceDetails.isInvoiceDebt) {
    //if invoice payment
    return [
      {
        content: formInputs?.introCoverTextInvoice?.label?.value,
      },
    ] as IHighlight[];
  } else {
    const highlights = [
      //roadside/lifestyle payment
      {
        content: replaceBookAssistanceBookMarks(
          formInputs?.introCoverTextAssistance?.label?.value,
          paymentReferenceDetails,
          isUpgrade
        ),
      },
      {
        content: formInputs?.introCoverTextAssistance2?.label?.value,
      },
    ] as IHighlight[];
    if (isUpgrade) {
      const upgradeText = formInputs?.['upgradeCoverTextAssistance' + productCode];
      highlights.splice(1, 0, {
        content: upgradeText ? upgradeText?.label?.value : formInputs?.upgradeCoverTextAssistance?.label?.value,
      });
    }

    return highlights;
  }
};

export const getBaseAnalyticsFormModel = (
  paymentReferenceDetails: IPaymentReferenceDetails,
  dictionary: Record<string, string>,
  quotedOption?: IQuotedOption
): IAnalyticsForm => {
  return {
    amount: paymentReferenceDetails?.amount ?? 0,
    productCategory: paymentReferenceDetails.description,
    productName: getProductType(paymentReferenceDetails, dictionary, quotedOption),
    detail: {
      vehicleDetail: paymentReferenceDetails?.hasVehicleDetails ? (paymentReferenceDetails?.policyDetail ?? '') : '',
    },
  };
};

const iconMap = {
  homeIcon: require('@/assets/icons/house-home.svg'),
  carIcon: require('@/assets/icons/car.svg'),
  petIcon: require('@/assets/icons/pet-paw.svg'),
  boatIcon: require('@/assets/icons/boat_icon.svg'),
  caravanIcon: require('@/assets/icons/caravan_icon.svg'),
  motorCycleIcon: require('@/assets/icons/motorcycle_icon.svg'),
  lifeStyleIcon: require('@/assets/icons/lifestyle_icon.svg'),
  successIcon: require('@/assets/icons/green-tick.svg'),
};

const successIconMap = {
  homeIcon: require('@/assets/icons/home-icon.svg'),
  carIcon: require('@/assets/icons/car-ticked.svg'),
  petIcon: require('@/assets/icons/pet-ticked.svg'),
  boatIcon: require('@/assets/icons/boat-ticked.svg'),
  caravanIcon: require('@/assets/icons/caravan-ticked.svg'),
  motorCycleIcon: require('@/assets/icons/motorcycle-ticked.svg'),
  lifeStyleIcon: require('@/assets/icons/lifestyle-ticked.svg'),
  successIcon: require('@/assets/icons/green-tick.svg'),
};

export const getProductTypeIcon = (
  paymentReferenceDetails: IPaymentReferenceDetails,
  isLifeStyle?: boolean,
  isSuccess?: boolean
) => {
  if (isLifeStyle) return isSuccess ? successIconMap.lifeStyleIcon : iconMap.lifeStyleIcon;
  if (paymentReferenceDetails.isInvoiceDebt) return iconMap.successIcon;
  //if multi coverage policy
  if (paymentReferenceDetails.coverageCode && paymentReferenceDetails.policyTitle) return iconMap.successIcon;
  if (paymentReferenceDetails.coverageCode) {
    switch (paymentReferenceDetails.coverageCode) {
      case QuoteTypeCodes[InsuranceQuoteType.Contents]:
      case QuoteTypeCodes[InsuranceQuoteType.Home]:
      case QuoteTypeCodes[InsuranceQuoteType.HomeAndContents]:
        return isSuccess ? successIconMap.homeIcon : iconMap.homeIcon;
      case QuoteTypeCodes[InsuranceQuoteType.MotorComprehensive]:
      case QuoteTypeCodes[InsuranceQuoteType.MotorFireTheftAndThirdPartyLiability]:
      case QuoteTypeCodes[InsuranceQuoteType.MotorThirdPartyLiability]:
        return isSuccess ? successIconMap.carIcon : iconMap.carIcon;
      case QuoteTypeCodes[InsuranceQuoteType.PetComp]:
        return isSuccess ? successIconMap.petIcon : iconMap.petIcon;
      case QuoteTypeCodes[InsuranceQuoteType.BoatComprehensive]:
      case QuoteTypeCodes[InsuranceQuoteType.BoatThirdParty]:
        return isSuccess ? successIconMap.boatIcon : iconMap.boatIcon;
      case QuoteTypeCodes[InsuranceQuoteType.Caravan]:
        return isSuccess ? successIconMap.caravanIcon : iconMap.caravanIcon;
      case QuoteTypeCodes[InsuranceQuoteType.Motorcycle]:
        return isSuccess ? successIconMap.motorCycleIcon : iconMap.motorCycleIcon;
    }
  }
  return isSuccess ? successIconMap.carIcon : iconMap.carIcon;
};
